<script lang="ts">
  import CryptoDeposit from "$lib/components/ModalContents/DepositWithdraw/Deposit/CryptoDeposit.svelte";
  import type {IUser} from "$lib/interfaces/IUserMe";
  import {toasts} from "$lib/stores/toastStores";
  import {openDialog} from "$lib/stores/dialogStores";
  import {user, DetectedCountry} from "$lib/stores";
  import BuyCryptoDeposit from "$lib/components/ModalContents/DepositWithdraw/Deposit/BuyCryptoDeposit.svelte";
  import {onMount} from "svelte";
  import {paymentService} from "$lib/services/payment.service";
  import type {IPaymentOption} from "$lib/interfaces/IPaymentOption";
  import CustomPayment from "$lib/components/ModalContents/DepositWithdraw/Deposit/CustomPayment.svelte";
  import PayopDeposit from "$lib/components/ModalContents/DepositWithdraw/Deposit/PayopDeposit.svelte";
  import { geolocationService } from "$lib/services/geolocation.service";
  import { browser } from "$app/environment";
  import { get } from 'svelte/store';

  interface Props {
    localUser: IUser;
  }

  // Payment option image IDs
  const BANK_TRANSFER_IMAGE_ID = 870;
  const SECOND_IMAGE_ID_TO_HIDE = 1051;
  const PAYOP_IMAGE_ID = 1050;

  // Create array of image IDs to hide
  const HIDDEN_IMAGE_IDS = [BANK_TRANSFER_IMAGE_ID, SECOND_IMAGE_ID_TO_HIDE];

  // Countries where bank transfer should be prioritized (converted to uppercase for consistency)
  const BANK_TRANSFER_PRIORITY_COUNTRIES = [
    'UK', 'GB', 'ES', 'AT', 'IE', 'NL', 'LT', 'BR', 'EE', 'FR', 'IT', 'MX', 'PL', 'FI'
  ];

  let paymentOptions: IPaymentOption[] = $state([]);
  let selectedPaymentOption: IPaymentOption | null = $state(null);
  let loading = $state(false);
  let sortedPaymentOptions = $state([]);
  let detectedCountry = $state('');
  let countryDetectionInProgress = $state(false);

  // Subscribe to the DetectedCountry store
  DetectedCountry.subscribe(value => {
    if (value) {
      detectedCountry = value.toUpperCase();


      // Re-sort payment options when country changes
      if (paymentOptions.length > 0) {
        sortedPaymentOptions = sortPaymentOptions(paymentOptions, detectedCountry);
      }
    }
  });

  function setDepositType(paymentOption: IPaymentOption) {
    if (paymentOption.personalInformationRequired) {
      if(!get(user)?.miniKycPassed) {
        openDialog('MiniKYCModal')
        return toasts.send('Please complete your Personal Information before making a deposit', 'info');
      }
    }
    selectedPaymentOption = paymentOption;
    setTimeout(() => {
      loading = false;
    }, 100);
  }

  function scrollToActive(target:any, paymentOption: IPaymentOption) {
    loading = true
    selectedPaymentOption = null;
    setDepositType(paymentOption);
    target.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    });
  }

  // Sort payment options based on country and filter out hidden options
  function sortPaymentOptions(options: IPaymentOption[], country: string): IPaymentOption[] {
    if (!options || options.length === 0) return [];

    // Filter out options with hidden image IDs
    const filteredOptions = options.filter(option => !HIDDEN_IMAGE_IDS.includes(option.image?.id));

    // Make a copy to avoid mutating the filtered array
    const sortedOptions = [...filteredOptions];

    // Check if user's country is in the priority list
    const shouldPrioritizeBankTransfer = BANK_TRANSFER_PRIORITY_COUNTRIES.includes(country);

    if (shouldPrioritizeBankTransfer) {
      // Find all payment options that should be prioritized
      const bankTransferOptions = sortedOptions.filter(option => {
        // Check if this is a bank transfer option by image ID or title
        const isBankTransfer =
                option.image?.id === PAYOP_IMAGE_ID ||
                option.title?.toLowerCase().includes('bank') ||
                option.title?.toLowerCase().includes('transfer');
        return isBankTransfer;
      });

      if (bankTransferOptions.length > 0) {

        // Remove bank transfer options from the array
        bankTransferOptions.forEach(option => {
          const index = sortedOptions.findIndex(o => o.id === option.id);
          if (index !== -1) {
            sortedOptions.splice(index, 1);
          }
        });

        // Add them to the beginning
        sortedOptions.unshift(...bankTransferOptions);

      } else {
        console.log("No bank transfer options found");
      }
    }

    return sortedOptions;
  }

  // Function to detect country using client-side methods only
  async function detectCountryClientSide() {
    if (!browser || countryDetectionInProgress) return;

    countryDetectionInProgress = true;

    try {
      try {
        const geoResponse = await fetch("https://ipapi.co/json/");
        const geoData = await geoResponse.json();

        if (geoData.country_code) {
          DetectedCountry.set(geoData.country_code);
          return;
        }
      } catch (error) {
        console.error("Error with ipapi.co:", error);
      }

      // Try ipinfo.io as backup
      try {
        const geoResponse2 = await fetch("https://ipinfo.io/json");
        const geoData2 = await geoResponse2.json();

        if (geoData2.country) {
          DetectedCountry.set(geoData2.country);
          return;
        }
      } catch (error) {
        console.error("Error with ipinfo.io:", error);
      }

      // If all else fails, try a third service
      try {
        const geoResponse3 = await fetch("https://geolocation-db.com/json/");
        const geoData3 = await geoResponse3.json();

        if (geoData3.country_code) {
          DetectedCountry.set(geoData3.country_code);
          return;
        }
      } catch (error) {
        console.error("Error with geolocation-db:", error);
      }

    } catch (error) {
      console.error("Error in client-side country detection:", error);
    } finally {
      countryDetectionInProgress = false;
    }
  }

  // Force client-side country detection on component initialization
  async function initializeCountryDetection() {
    // Always use client-side detection first
    await detectCountryClientSide();

    // If client-side detection failed, try the geolocation service as fallback
    if (!get(DetectedCountry)) {
      try {
        const country = await geolocationService.detectCountry();
        if (country && country !== 'NL') {
          DetectedCountry.set(country);
        }
      } catch (error) {
        console.error("Error in geolocation service:", error);
      }
    }

    // If we still don't have a country, set a default
    if (!get(DetectedCountry)) {
      DetectedCountry.set('UK');
    }
  }

  onMount(async () => {
    // Initialize country detection
    await initializeCountryDetection();

    // Fetch payment options
    try {
      const res = await paymentService.getPaymentOptions('DEPOSIT');
      paymentOptions = res.data.data;

      // Sort payment options based on detected country
      sortedPaymentOptions = sortPaymentOptions(paymentOptions, detectedCountry);
    } catch (error) {
      console.error("Error fetching payment options:", error);
      toasts.send('Error loading payment options', 'error');
    }
  });

  // Update sorted options whenever paymentOptions or detectedCountry changes
  $effect(() => {
    if (paymentOptions.length > 0 && detectedCountry) {
      sortedPaymentOptions = sortPaymentOptions(paymentOptions, detectedCountry);
    }
  });
</script>

<div class="bg-primary-dark p-2 md:p-4 rounded-2xl gap-2 md:gap-5 w-full {selectedPaymentOption ? 'flex items-center overflow-x-auto' : 'grid grid-cols-2 lg:grid-cols-3'}">
  {#each sortedPaymentOptions as payment}
    <button on:click={(e) => scrollToActive(e.target, payment)} class="text-center overflow-hidden border-2 transition-all duration-200 min-w-36 {selectedPaymentOption ? 'flex-shrink-0' : 'col-span-1'} {selectedPaymentOption?.id === payment.id ? 'border-2 border-primary-fire rounded-lg' : 'border-transparent'}">
      <div class="bg-white p-2 h-12 md:h-16 flex items-center justify-around rounded">
        <img src={payment.image?.fileUrl || "/placeholder.svg"} alt={payment.title} class="w-full h-full object-contain">
      </div>
      <h5 class="text-xs md:text-sm py-2">{payment.title}</h5>
    </button>
  {/each}
</div>

{#if !loading}
  {#if (selectedPaymentOption?.type === 'CUSTOM')}
    <CustomPayment {selectedPaymentOption} />
  {:else if (selectedPaymentOption?.type === 'CRYPTO')}
    <CryptoDeposit />
  {:else if (selectedPaymentOption?.type === 'BUY_CRYPTO')}
    <BuyCryptoDeposit />
  {:else if (selectedPaymentOption?.type === 'PAY_OP_CUSTOM')}
    <PayopDeposit {selectedPaymentOption} />
  {:else if (selectedPaymentOption?.type === 'OMNO_CUSTOM')}
    <PayopDeposit {selectedPaymentOption}/>
  {:else}
    <div class="bg-primary-dark p-2 md:p-4 rounded-2xl w-full mt-2">
      <h5 class="text-white text-center">Please select a deposit method</h5>
    </div>
  {/if}
{:else}
  <div class="bg-primary-dark p-2 md:p-4 rounded-2xl w-full mt-2">
    <h5 class="text-white text-center">Loading...</h5>
  </div>
{/if}